import { InterpolatedProperty, SimpleSettingsProperty } from '../properties';
import { UUID, Color, Vector3 } from '../types';
import { ScreenPosition } from '../shapes/ScreenHTMLStaticLabelMesh';

/**
 * 🎯 SceneSettingsData - Scene-specific settings with hierarchical properties
 * Contains rendering, animation, material, and POI default settings
 * Singleton pattern for global access
 */
export class SceneSettingsData {
    private static _instance: SceneSettingsData | null = null;
    
    public static get Instance(): SceneSettingsData {
        if (!SceneSettingsData._instance) {
            SceneSettingsData._instance = new SceneSettingsData();
        }
        return SceneSettingsData._instance;
    }
    
    public static setInstance(instance: SceneSettingsData): void {
        SceneSettingsData._instance = instance;
    }
    
    public readonly id: UUID;
    
    // 🎨 Rendering settings
    public readonly billboardFontSize: InterpolatedProperty<number>;
    public readonly billboardFontFamily: SimpleSettingsProperty<string>;
    public readonly billboardBackgroundColor: InterpolatedProperty<Color>;
    public readonly backgroundGradientColor1: InterpolatedProperty<Color>;
    public readonly backgroundGradientColor2: InterpolatedProperty<Color>;
    public readonly backgroundGradientAngle: InterpolatedProperty<number>;
    public readonly textBackgroundMode: SimpleSettingsProperty<'block' | 'line' | 'none'>;
    public readonly textResolutionFactor: InterpolatedProperty<number>;
    public readonly renderQuality: SimpleSettingsProperty<'low' | 'medium' | 'high' | 'ultra'>;
    public readonly antialiasing: SimpleSettingsProperty<boolean>;
    public readonly shadows: SimpleSettingsProperty<boolean>;
    public readonly geometryResolution: SimpleSettingsProperty<number>;
    public readonly lodBias: InterpolatedProperty<number>;          // ✨ Simplified: lodBias instead of globalLodBias
    
    // 💡 Lighting settings
    public readonly ambientLightColor: InterpolatedProperty<Color>;
    public readonly ambientLightIntensity: InterpolatedProperty<number>;
    
    public readonly keyLightColor: InterpolatedProperty<Color>;
    public readonly keyLightIntensity: InterpolatedProperty<number>;
    public readonly keyLightPosition: SimpleSettingsProperty<Vector3>;
    
    public readonly fillLightColor: InterpolatedProperty<Color>;
    public readonly fillLightIntensity: InterpolatedProperty<number>;
    public readonly fillLightPosition: SimpleSettingsProperty<Vector3>;

    public readonly backLightColor: InterpolatedProperty<Color>;
    public readonly backLightIntensity: InterpolatedProperty<number>;
    public readonly backLightPosition: SimpleSettingsProperty<Vector3>;
    
    // 🎬 Animation settings
    public readonly animationSpeed: InterpolatedProperty<number>;
    public readonly transitionDuration: InterpolatedProperty<number>;
    public readonly enableAnimations: SimpleSettingsProperty<boolean>;
    public readonly smoothTransitions: SimpleSettingsProperty<boolean>;
    
    // 🛤️ Process Curve settings
    public readonly showProcessCurve: SimpleSettingsProperty<boolean>;
    public readonly processCurveColor: SimpleSettingsProperty<Color>;
    public readonly processCurveThickness: InterpolatedProperty<number>;
    public readonly processCurveMarkerSize: InterpolatedProperty<number>;
    public readonly showProcessNumbers: SimpleSettingsProperty<boolean>;
    public readonly processNumberColor: SimpleSettingsProperty<Color>;

    public readonly showProcessMarkerSpheres: SimpleSettingsProperty<boolean>;
    public readonly processNumberFontSize: InterpolatedProperty<number>;
    public readonly processNumberFontFamily: SimpleSettingsProperty<string>;
    public readonly processNumberFontWeight: SimpleSettingsProperty<string>;
    public readonly processNumberLineOffset: InterpolatedProperty<number>;
    public readonly processBillboardScale: InterpolatedProperty<number>;
    public readonly processBillboardBackgroundColor: SimpleSettingsProperty<Color>;
    public readonly processBillboardBorderColor: SimpleSettingsProperty<Color>;
    public readonly showProcessStartMarker: SimpleSettingsProperty<boolean>;
    public readonly processStartMarkerText: SimpleSettingsProperty<string>;
    // 📈 Track/Morphline data (serialized JSON of projected path points)
    public readonly morphlineJson: SimpleSettingsProperty<string>;
    // 🔗 ARTrack lazy-load hints
    public readonly artrackTrackId: SimpleSettingsProperty<string>;
    public readonly artrackUseUTM: SimpleSettingsProperty<boolean>;
    public readonly artrackUtmZone: SimpleSettingsProperty<string>;
    public readonly artrackAltitudeScale: SimpleSettingsProperty<number>;
    public readonly artrackTrackScale: SimpleSettingsProperty<number>;
    public readonly showMorphline: SimpleSettingsProperty<boolean>;
    
    // 🌍 Scene-wide properties (simplified names for inheritance)
    public readonly scale: InterpolatedProperty<number>;            // ✨ Simplified: scale instead of globalScale
    public readonly opacity: InterpolatedProperty<number>;          // ✨ Simplified: opacity instead of globalOpacity
    
    // 🎯 CLEAN: Primary/Secondary Material System 
    // Only roughness/metalness get primary/secondary - color stays unified!
    public readonly primaryRoughness: InterpolatedProperty<number>;
    public readonly primaryMetalness: InterpolatedProperty<number>;
    public readonly secondaryRoughness: InterpolatedProperty<number>;
    public readonly secondaryMetalness: InterpolatedProperty<number>;
    
    // 🎨 Theme settings
    public readonly primaryColor: InterpolatedProperty<Color>;
    public readonly secondaryColor: InterpolatedProperty<Color>;
    public readonly accentColor: InterpolatedProperty<Color>;
    public readonly textColor: InterpolatedProperty<Color>;
    
    // 🏗️ Material Properties (simplified - no more presets!)
    // Note: Only globalRoughness and globalMetalness remain - POIs inherit these directly
    
    // 📚 Layer settings (inherit from scene globals)
    public readonly backgroundOpacity: InterpolatedProperty<number>;
    public readonly backgroundScale: InterpolatedProperty<number>;
    public readonly foregroundOpacity: InterpolatedProperty<number>;
    public readonly foregroundScale: InterpolatedProperty<number>;
    public readonly uiOpacity: InterpolatedProperty<number>;
    public readonly uiScale: InterpolatedProperty<number>;
    
    // 🎮 POI default settings (for inheritance by POIData)
    public readonly shape: SimpleSettingsProperty<string>;           // ✨ NEW: Default shape for all POIs
    public readonly color: InterpolatedProperty<Color>;
    public readonly poiOpacity: InterpolatedProperty<number>;        // Renamed to avoid conflict with scene opacity
    public readonly poiScale: InterpolatedProperty<number>;          // Renamed to avoid conflict with scene scale
    public readonly cornerRadius: InterpolatedProperty<number>;
    public readonly edgeRadius: InterpolatedProperty<number>;
    public readonly wiggleAmplitude: InterpolatedProperty<number>;
    public readonly wiggleSpeed: InterpolatedProperty<number>;
    public readonly rotationSpeed: InterpolatedProperty<number>;
    
    // 🎮 Additional POI default settings (missing from POIData global inheritance)
    public readonly depth: InterpolatedProperty<number>;
    public readonly aspectRatio: InterpolatedProperty<number>;
    public readonly showLabel: SimpleSettingsProperty<boolean>;
    public readonly labelScreenPosition: SimpleSettingsProperty<ScreenPosition>;
    
    // 🎬 Video/Media default settings
    public readonly videoAutoplay: SimpleSettingsProperty<boolean>;
    public readonly videoLoop: SimpleSettingsProperty<boolean>;
    public readonly videoMuted: SimpleSettingsProperty<boolean>;
    
    // 🎭 Animation default settings
    public readonly enableBillboard: SimpleSettingsProperty<boolean>;
    public readonly enableRotation: SimpleSettingsProperty<boolean>;
    public readonly enableWiggle: SimpleSettingsProperty<boolean>;
    public readonly wiggleHeight: InterpolatedProperty<number>;
    public readonly wiggleX: SimpleSettingsProperty<boolean>;
    public readonly wiggleY: SimpleSettingsProperty<boolean>;
    public readonly wiggleZ: SimpleSettingsProperty<boolean>;
    
    // 🎲 Randomization settings
    public readonly randomTexture: SimpleSettingsProperty<boolean>;
    public readonly randomColor: SimpleSettingsProperty<boolean>;
    
    // 🖼️ Global image material settings
    public readonly useBackgroundColor: SimpleSettingsProperty<boolean>;
    public readonly useBacksideSideMaterial: SimpleSettingsProperty<boolean>;
    
    // Depth of Field
    public readonly dofEnabled: SimpleSettingsProperty<boolean>;
    public readonly dofFocus: InterpolatedProperty<number>;     // Focus distance (auto-follows active POI if 0)
    public readonly dofAperture: InterpolatedProperty<number>;  // Lens aperture (higher = more blur)
    public readonly dofMaxBlur: InterpolatedProperty<number>;   // Maximum blur amount

    // Additional PBR Material Properties (already initialized but missing declarations)
    public readonly emissive: InterpolatedProperty<Color>;
    public readonly emissiveIntensity: InterpolatedProperty<number>;
    public readonly wireframe: SimpleSettingsProperty<boolean>;
    public readonly depthTest: SimpleSettingsProperty<boolean>;
    public readonly depthWrite: SimpleSettingsProperty<boolean>;
    public readonly transparent: SimpleSettingsProperty<boolean>;
    
    constructor() {
        this.id = crypto.randomUUID();
        
        // 🎨 Initialize rendering settings (no inheritance)
        this.billboardFontSize = new InterpolatedProperty('Billboard Font Size', null, 14, 0.5, 'Rendering').withBounds(8, 32, 1);
        this.billboardFontFamily = new SimpleSettingsProperty('Billboard Font Family', null, 'Arial', 'Rendering', ['Arial', 'Helvetica', 'Times New Roman', 'Courier New', 'Verdana']);
        this.billboardBackgroundColor = new InterpolatedProperty('Billboard Background Color', null, new Color('#000000'), 0.2, 'Rendering');
        
        // Background Gradient
        const backgroundGroup = 'Background';
        this.backgroundGradientColor1 = new InterpolatedProperty('Gradient Color 1', null, new Color('#1a2a6c'), 0.5, backgroundGroup);
        this.backgroundGradientColor2 = new InterpolatedProperty('Gradient Color 2', null, new Color('#b21f1f'), 0.5, backgroundGroup);
        this.backgroundGradientAngle = new InterpolatedProperty('Gradient Angle', null, 45, 0.5, backgroundGroup).withBounds(0, 360, 1);

        this.textBackgroundMode = new SimpleSettingsProperty<'block' | 'line' | 'none'>('Text Background Mode', null, 'line', 'Rendering', ['block', 'line', 'none']);
        this.textResolutionFactor = new InterpolatedProperty('Text Resolution Factor', null, 2.0, 0.3, 'Rendering').withBounds(0.5, 4, 0.1);
        this.renderQuality = new SimpleSettingsProperty<'low' | 'medium' | 'high' | 'ultra'>('Render Quality', null, 'high', 'Rendering', ['low', 'medium', 'high', 'ultra']);
        this.antialiasing = new SimpleSettingsProperty('Antialiasing', null, true, 'Rendering');
        this.shadows = new SimpleSettingsProperty('Shadows', null, true, 'Rendering');
        // Default 2 (was 8): at 8 every rounded tile tessellated to ~200k vertices / 6.4MB —
        // 45 map pins alone ate 284MB JS heap and OOM-killed mobile tabs. At 2 a tile is a
        // few thousand vertices and visually identical at presentation scales. Presentations
        // can still raise this via the geometryResolution markdown setting.
        this.geometryResolution = new SimpleSettingsProperty('Geometry Resolution', null, 2, 'Rendering').withBounds(1, 20, 0.01);
        this.lodBias = new InterpolatedProperty('LOD Bias', null, 0, 0.1, 'Rendering').withBounds(-2, 2, 0.05);
        
        // 💡 Initialize lighting settings
        const lightingGroup = 'Lighting';
        this.ambientLightColor = new InterpolatedProperty('Ambient Color', null, new Color('#ffffff'), 0.3, lightingGroup);
        this.ambientLightIntensity = new InterpolatedProperty('Ambient Intensity', null, 0.5, 2, lightingGroup).withBounds(0, 2, 0.05);

        this.keyLightColor = new InterpolatedProperty('Key Light Color', null, new Color('#ffffff'), 0.3, lightingGroup);
        this.keyLightIntensity = new InterpolatedProperty('Key Light Intensity', null, 2.0, 2, lightingGroup).withBounds(0, 5, 0.01);
        this.keyLightPosition = new SimpleSettingsProperty('Key Light Position', null, new Vector3(5, 5, 5), lightingGroup);

        this.fillLightColor = new InterpolatedProperty('Fill Light Color', null, new Color('#ffffff'), 0.3, lightingGroup);
        this.fillLightIntensity = new InterpolatedProperty('Fill Light Intensity', null, 2, 2, lightingGroup).withBounds(0, 5, 0.01);
        this.fillLightPosition = new SimpleSettingsProperty('Fill Light Position', null, new Vector3(-5, 2, 5), lightingGroup);
        
        this.backLightColor = new InterpolatedProperty('Back Light Color', null, new Color('#ffffff'), 0.3, lightingGroup);
        this.backLightIntensity = new InterpolatedProperty('Back Light Intensity', null, 2, 2, lightingGroup).withBounds(0, 5, 0.01);
        this.backLightPosition = new SimpleSettingsProperty('Back Light Position', null, new Vector3(0, 5, -5), lightingGroup);

        // 🎬 Initialize animation settings (no inheritance)
        this.animationSpeed = new InterpolatedProperty('Animation Speed', null, 1.0, 0.5, 'Animation').withBounds(0.1, 3, 0.1);
        this.transitionDuration = new InterpolatedProperty('Transition Duration', null, 1.0, 0.3, 'Animation').withBounds(0.1, 5, 0.1);
        this.enableAnimations = new SimpleSettingsProperty('Enable Animations', null, true, 'Animation');
        this.smoothTransitions = new SimpleSettingsProperty('Smooth Transitions', null, true, 'Animation');
        
        // 🛤️ Initialize Process Curve settings
        const processCurveGroup = 'Process Curve';
        this.showProcessCurve = new SimpleSettingsProperty('Show Process Curve', null, false, processCurveGroup);
        this.processCurveColor = new SimpleSettingsProperty('Curve Color', null, new Color('#ffffff'), processCurveGroup);
        this.processCurveThickness = new InterpolatedProperty('Curve Thickness', null, 0.15, 0.2, processCurveGroup).withBounds(0.05, 0.5, 0.01);
        this.processCurveMarkerSize = new InterpolatedProperty('Marker Size', null, 0.3, 0.2, processCurveGroup).withBounds(0.1, 1.0, 0.05);
        this.showProcessNumbers = new SimpleSettingsProperty('Show Numbers', null, true, processCurveGroup);
        this.processNumberColor = new SimpleSettingsProperty('Number Color', null, new Color('#000000'), processCurveGroup);

        this.showProcessMarkerSpheres = new SimpleSettingsProperty('Show Marker Spheres', null, false, processCurveGroup);
        this.processNumberFontSize = new InterpolatedProperty('Font Size', null, 48, 0.2, processCurveGroup).withBounds(12, 120, 2);
        this.processNumberFontFamily = new SimpleSettingsProperty('Font Family', null, 'Arial', processCurveGroup, ['Arial', 'Helvetica', 'Times New Roman', 'Courier New', 'Verdana']);
        this.processNumberFontWeight = new SimpleSettingsProperty('Font Weight', null, 'bold', processCurveGroup, ['normal', 'bold', '100', '200', '300', '400', '500', '600', '700', '800', '900']);
        this.processNumberLineOffset = new InterpolatedProperty('Number Line Position', null, 0, -0.2, processCurveGroup).withBounds(-1.0, 1.0, 0.001);
        this.processBillboardScale = new InterpolatedProperty('Billboard Scale', null, 0.5, 0.2, processCurveGroup).withBounds(0.1, 3.0, 0.1);
        this.processBillboardBackgroundColor = new SimpleSettingsProperty('Billboard Background', null, new Color('#ffffff'), processCurveGroup);
        this.processBillboardBorderColor = new SimpleSettingsProperty('Billboard Border', null, new Color('#000000'), processCurveGroup);
        this.showProcessStartMarker = new SimpleSettingsProperty('Show Start Marker', null, true, processCurveGroup);
        this.processStartMarkerText = new SimpleSettingsProperty('Start Marker Text', null, 'S', processCurveGroup);
        this.morphlineJson = new SimpleSettingsProperty('Morphline JSON', null, '', processCurveGroup).setMultiline?.(true) ?? new SimpleSettingsProperty('Morphline JSON', null, '', processCurveGroup);
        this.artrackTrackId = new SimpleSettingsProperty('ARTrack Track ID', null, '', processCurveGroup);
        this.artrackUseUTM = new SimpleSettingsProperty('ARTrack Use UTM', null, true, processCurveGroup);
        this.artrackUtmZone = new SimpleSettingsProperty('ARTrack UTM Zone', null, '', processCurveGroup);
        this.artrackAltitudeScale = new SimpleSettingsProperty('ARTrack Altitude Scale', null, 1.0, processCurveGroup).withBounds?.(0, 100, 0.1) ?? new SimpleSettingsProperty('ARTrack Altitude Scale', null, 1.0, processCurveGroup);
        this.artrackTrackScale = new SimpleSettingsProperty('ARTrack Track Scale', null, 1.0, processCurveGroup).withBounds?.(0, 1000, 0.1) ?? new SimpleSettingsProperty('ARTrack Track Scale', null, 1.0, processCurveGroup);
        this.showMorphline = new SimpleSettingsProperty('Show Morphline', null, true, processCurveGroup);
        
        // Depth of Field
        const dofGroup = 'Depth of Field';
        this.dofEnabled = new SimpleSettingsProperty('DOF Enabled', null, false, dofGroup);
        this.dofFocus = new InterpolatedProperty('DOF Focus Distance', null, 0, 0.3, dofGroup).withBounds(0, 500, 0.1);
        this.dofAperture = new InterpolatedProperty('DOF Aperture', null, 0.025, 0.3, dofGroup).withBounds(0, 0.1, 0.001);
        this.dofMaxBlur = new InterpolatedProperty('DOF Max Blur', null, 0.01, 0.3, dofGroup).withBounds(0, 0.1, 0.001);

        // Initialize scene-wide material properties (TOP-LEVEL PARENTS for POIs)
        this.scale = new InterpolatedProperty('Scene Scale', null, 1.0, 0.5, 'Scene');
        this.opacity = new InterpolatedProperty('Scene Opacity', null, 1.0, 0.2, 'Scene').withBounds(0, 1, 0.01);
        
        // 🎯 Initialize separate material properties (global defaults for POIs)
        this.primaryRoughness = new InterpolatedProperty('Primary Material Roughness', null, 0.5, 0.3, 'Material').withBounds(0, 1, 0.01);
        this.primaryMetalness = new InterpolatedProperty('Primary Material Metalness', null, 0.0, 0.3, 'Material').withBounds(0, 1, 0.01);
        this.secondaryRoughness = new InterpolatedProperty('Secondary Material Roughness', null, 0.5, 0.3, 'Material').withBounds(0, 1, 0.01);
        this.secondaryMetalness = new InterpolatedProperty('Secondary Material Metalness', null, 0.0, 0.3, 'Material').withBounds(0, 1, 0.01);
        
        // 🎯 Additional Material Properties (global defaults)
        this.emissive = new InterpolatedProperty('Emissive Color', null, new Color('#000000'), 0.3, 'Material');
        this.emissiveIntensity = new InterpolatedProperty('Emissive Intensity', null, 0.0, 1.0, 'Material').withBounds(0, 10, 0.01);
        this.wireframe = new SimpleSettingsProperty('Wireframe', null, false, 'Material');
        this.depthTest = new SimpleSettingsProperty('Depth Test', null, true, 'Material');
        this.depthWrite = new SimpleSettingsProperty('Depth Write', null, true, 'Material');
        this.transparent = new SimpleSettingsProperty('Transparent', null, false, 'Material');
        
        // 🎨 Initialize theme settings (can have initial values, but no parents)
        this.primaryColor = new InterpolatedProperty('Primary Color', null, new Color('#ffffff'), 0.3, 'Theme');
        this.secondaryColor = new InterpolatedProperty('Secondary Color', null, new Color('#cccccc'), 0.3, 'Theme');
        this.accentColor = new InterpolatedProperty('Accent Color', null, new Color('#007acc'), 0.3, 'Theme');
        this.textColor = new InterpolatedProperty('Text Color', null, new Color('#ffffff'), 0.3, 'Theme');
        
        // 🏗️ Material Properties: Simplified! 
        // POIs inherit directly from globalRoughness and globalMetalness
        // No more material presets needed - cleaner architecture!
        
        // 📚 Initialize layer settings (inherit from scene globals)
        this.backgroundOpacity = new InterpolatedProperty('Background Opacity', null, null, 0.2, 'Layers', this.opacity).withBounds(0, 1, 0.01);
        this.backgroundScale = new InterpolatedProperty('Background Scale', null, null, 0.5, 'Layers', this.scale).withBounds(0.1, 10, 0.1);
        this.foregroundOpacity = new InterpolatedProperty('Foreground Opacity', null, null, 0.2, 'Layers', this.opacity).withBounds(0, 1, 0.01);
        this.foregroundScale = new InterpolatedProperty('Foreground Scale', null, null, 0.5, 'Layers', this.scale).withBounds(0.1, 10, 0.1);
        this.uiOpacity = new InterpolatedProperty('UI Opacity', null, 0.9, 0.1, 'Layers').withBounds(0, 1, 0.01);
        this.uiScale = new InterpolatedProperty('UI Scale', null, 1.0, 0.2, 'Layers').withBounds(0.5, 2, 0.05);
        
        // 🎮 Initialize POI default settings (for inheritance by POIData)
        this.shape = new SimpleSettingsProperty('POI Default Shape', null, 'box', 'POI', [
            'box', 'sphere', 'cylinder', 'plane', 'multiplane', 'torus', 'triangle', 'hexagon', 'quader', 'tile', 'gltf', 'usdz', 'youtube'
        ]);
        this.color = new InterpolatedProperty('POI Default Color', null, new Color(), 0.3, 'POI', this.primaryColor);
        this.poiOpacity = new InterpolatedProperty('POI Default Opacity', null, null, 0.2, 'POI', this.opacity).withBounds(0, 1, 0.01);
        this.poiScale = new InterpolatedProperty('POI Default Scale', null, null, 0.3, 'POI', this.scale);

        this.cornerRadius = new InterpolatedProperty('POI Default Corner Radius', null, 0.1, 0.2, 'POI').withBounds(0, 1, 0.01);
        this.edgeRadius = new InterpolatedProperty('POI Default Edge Radius', null, 0.1, 0.2, 'POI').withBounds(0, 1, 0.01);
        this.wiggleAmplitude = new InterpolatedProperty('POI Default Wiggle Amplitude', null, 0.1, 0.2, 'POI').withBounds(0, 1, 0.01);
        this.wiggleSpeed = new InterpolatedProperty('POI Default Wiggle Speed', null, 1.0, 0.2, 'POI').withBounds(0, 5, 0.1);
        this.rotationSpeed = new InterpolatedProperty('POI Default Rotation Speed', null, 0.5, 0.2, 'POI').withBounds(-5, 5, 0.1);
        
        // 🎮 Initialize additional POI default settings
        this.depth = new InterpolatedProperty('POI Default Depth', null, 0.1, 0.3, 'POI').withBounds(0, 10, 0.01);
        this.aspectRatio = new InterpolatedProperty('POI Default Aspect Ratio', null, 0, 0.3, 'POI').withBounds(0.1, 10, 0.1); // 0 = auto
        this.showLabel = new SimpleSettingsProperty('POI Default Show Label', null, true, 'POI');
        this.labelScreenPosition = new SimpleSettingsProperty('POI Default Label Screen Position', null, ScreenPosition.TOP_RIGHT, 'POI', Object.values(ScreenPosition));
        
        // 🎬 Initialize video/media default settings
        this.videoAutoplay = new SimpleSettingsProperty('POI Default Video Autoplay', null, false, 'POI');
        this.videoLoop = new SimpleSettingsProperty('POI Default Video Loop', null, true, 'POI');
        this.videoMuted = new SimpleSettingsProperty('POI Default Video Muted', null, true, 'POI');
        
        // 🎭 Initialize animation default settings
        this.enableBillboard = new SimpleSettingsProperty('POI Default Shape Billboard Mode', null, false, 'POI');
        this.enableRotation = new SimpleSettingsProperty('POI Default Enable Rotation', null, false, 'POI');
        this.enableWiggle = new SimpleSettingsProperty('POI Default Enable Wiggle', null, false, 'POI');
        this.wiggleHeight = new InterpolatedProperty('POI Default Wiggle Height', null, 0.2, 0.3, 'POI').withBounds(0, 2, 0.05);
        this.wiggleX = new SimpleSettingsProperty('POI Default Wiggle X', null, true, 'POI');
        this.wiggleY = new SimpleSettingsProperty('POI Default Wiggle Y', null, true, 'POI');
        this.wiggleZ = new SimpleSettingsProperty('POI Default Wiggle Z', null, false, 'POI');
        
            // 🎲 Initialize randomization settings
    this.randomTexture = new SimpleSettingsProperty('Random Texture from Picsum', null, false, 'Randomization');
    this.randomColor = new SimpleSettingsProperty('Random Color Generation', null, false, 'Randomization');

    // 🖼️ Initialize global image material settings
            this.useBackgroundColor = new SimpleSettingsProperty('Use POI Color as Background for Images', null, true, 'Image Materials');
        this.useBacksideSideMaterial = new SimpleSettingsProperty('Use Side Material for Backside', null, false, 'Image Materials');
    }
    
    // 🎨 Theme presets
    applyTheme(theme: 'light' | 'dark' | 'blue' | 'neon'): void {
        switch (theme) {
            case 'light':
                this.primaryColor.value = new Color('#2a2a2a');
                this.textColor.value = new Color('#333333');
                this.accentColor.value = new Color('#007acc');
                break;
                
            case 'dark':
                this.primaryColor.value = new Color('#ffffff');
                this.textColor.value = new Color('#ffffff');
                this.accentColor.value = new Color('#00aaff');
                break;
                
            case 'blue':
                this.primaryColor.value = new Color('#007acc');
                this.textColor.value = new Color('#ccddff');
                this.accentColor.value = new Color('#00ffaa');
                break;
                
            case 'neon':
                this.primaryColor.value = new Color('#ff00ff');
                this.textColor.value = new Color('#00ffff');
                this.accentColor.value = new Color('#ffff00');
                break;
        }
    }
    
    // ⚡ Performance presets
    setPerformanceMode(mode: 'low' | 'medium' | 'high' | 'ultra'): void {
        switch (mode) {
            case 'low':
                this.primaryRoughness.value = 1.0;        // ✨ Now using simplified name
                this.primaryMetalness.value = 0.0;        // ✨ Now using simplified name
                this.secondaryRoughness.value = 1.0;        // ✨ Now using simplified name
                this.secondaryMetalness.value = 0.0;        // ✨ Now using simplified name
                this.renderQuality.value = 'low';
                this.antialiasing.value = false;
                this.shadows.value = false;
                break;
                
            case 'medium':
                this.primaryRoughness.value = 0.7;        // ✨ Now using simplified name
                this.primaryMetalness.value = 0.2;        // ✨ Now using simplified name
                this.secondaryRoughness.value = 0.7;        // ✨ Now using simplified name
                this.secondaryMetalness.value = 0.2;        // ✨ Now using simplified name
                this.renderQuality.value = 'medium';
                this.antialiasing.value = true;
                this.shadows.value = false;
                break;
                
            case 'high':
                this.primaryRoughness.value = 0.3;        // ✨ Now using simplified name
                this.primaryMetalness.value = 0.6;        // ✨ Now using simplified name
                this.secondaryRoughness.value = 0.3;        // ✨ Now using simplified name
                this.secondaryMetalness.value = 0.6;        // ✨ Now using simplified name
                this.renderQuality.value = 'high';
                this.antialiasing.value = true;
                this.shadows.value = true;
                break;
                
            case 'ultra':
                this.primaryRoughness.value = 0.1;        // ✨ Now using simplified name
                this.primaryMetalness.value = 0.9;        // ✨ Now using simplified name
                this.secondaryRoughness.value = 0.1;        // ✨ Now using simplified name
                this.secondaryMetalness.value = 0.9;        // ✨ Now using simplified name
                this.renderQuality.value = 'ultra';
                this.antialiasing.value = true;
                this.shadows.value = true;
                break;
        }
    }
    
    // 🎭 Layer visibility presets
    setLayerMode(mode: 'normal' | 'focus' | 'minimal'): void {
        switch (mode) {
            case 'normal':
                this.backgroundOpacity.value = 0.3;
                this.foregroundOpacity.value = 1.0;
                this.uiOpacity.value = 0.9;
                break;
                
            case 'focus':
                this.backgroundOpacity.value = 0.1;
                this.foregroundOpacity.value = 1.0;
                this.uiOpacity.value = 0.5;
                break;
                
            case 'minimal':
                this.backgroundOpacity.value = 0.05;
                this.foregroundOpacity.value = 0.8;
                this.uiOpacity.value = 0.3;
                break;
        }
    }
    
    // 🎯 Scene animations (simplified method names)
    animateScale(targetScale: number, duration: number = 1.0): void {
        //this.scale.setDuration(duration);
        this.scale.value = targetScale;
    }
    
    fade(targetOpacity: number, duration: number = 0.3): void {
        //this.opacity.setDuration(duration);
        this.opacity.value = targetOpacity;
    }
    


    /**
     * Get all properties grouped by their group names
     * Perfect for UI generation (same as AppSettingsData)
     */
    getGroupedProperties(): { [groupName: string]: { name: string; property: any }[] } {
        const groups: { [groupName: string]: { name: string; property: any }[] } = {};
        
        // Get all property names via reflection
        const propertyNames = Object.getOwnPropertyNames(this).filter(name => 
            this[name as keyof this] instanceof InterpolatedProperty || 
            this[name as keyof this] instanceof SimpleSettingsProperty
        );
        
        propertyNames.forEach(propertyName => {
            const property = this[propertyName as keyof this] as any;
            if (property && property.groupName) {
                const groupName = property.groupName;
                if (!groups[groupName]) {
                    groups[groupName] = [];
                }
                groups[groupName].push({ name: propertyName, property });
            }
        });
        
        return groups;
    }
    
    /**
     * Get all properties as flat list with metadata
     * (same as AppSettingsData - for consistency)
     */
    getAllProperties(): { name: string; property: any; group: string; type: string }[] {
        const grouped = this.getGroupedProperties();
        const result: { name: string; property: any; group: string; type: string }[] = [];
        
        Object.entries(grouped).forEach(([groupName, properties]) => {
            properties.forEach(({ name, property }) => {
                result.push({
                    name,
                    property,
                    group: groupName,
                    type: property.typeInfo.type
                });
            });
        });
        
        return result;
    }

} 